import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { ArrowRight, Mail, BarChart3, CheckCircle2, Clock, DollarSign, ShoppingCart, AlertTriangle, Shield } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect } from "react";

// Capture referral code from URL
const captureReferralCode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get("ref");
  if (refCode) {
    localStorage.setItem("referral_code", JSON.stringify({
      code: refCode,
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    }));
  }
};

const AgencyPage = () => {
  const { login, user } = useAuth();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");

  useEffect(() => {
    captureReferralCode();
  }, []);

  const handleGetStarted = () => {
    if (user) {
      window.location.href = "/onboarding";
    } else {
      sessionStorage.setItem('returnTo', '/onboarding');
      login();
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => scrollToSection('sample-report')}
              className="text-text-muted hover:text-white transition-colors text-sm hidden md:block"
            >
              Sample Report
            </button>
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-6 py-2">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Button
                onClick={login}
                className="bg-white/10 text-white hover:bg-white/20 border border-white/10 rounded-full px-6 py-2"
              >
                Sign In
              </Button>
            )}
          </div>
        </nav>
      </header>

      <div className="min-h-screen bg-[#050505] text-white pt-20">
        {/* Hero Section */}
        <section className="py-20 px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-[#D4F34A] mb-6 text-sm">
              <Shield className="w-4 h-4" />
              <span className="uppercase tracking-wider font-medium">For Agencies & Consultants</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
              A Weekly Profit Monitoring System<br />
              <span className="text-[#D4F34A]">for Shopify Stores</span>
            </h1>
            
            <p className="text-lg text-text-muted max-w-2xl mx-auto mb-8">
              Used by agencies and consultants to spot revenue leaks and give clients clear priorities - without manual analytics checks.
            </p>
            
            <Button
              onClick={handleGetStarted}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-8 py-6 text-lg transition-transform hover:scale-105"
              data-testid="agency-cta-btn"
            >
              Get a Weekly Profit Report (Free) <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <p className="text-sm text-text-muted mt-4">
              Read-only access · No store changes · No customer data pulled
            </p>
          </div>
        </section>

        {/* Why Agencies Use This */}
        <section className="py-16 px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-3xl font-bold tracking-tight mb-8 text-center">
              Why agencies use ReplyDriven
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">Replaces manual analytics checks</h3>
                <p className="text-sm text-text-muted">Stop spending hours in Shopify Analytics. Get the one thing that matters each week.</p>
              </div>
              
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <BarChart3 className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">Makes recommendations data-driven</h3>
                <p className="text-sm text-text-muted">Back up your advice with real numbers and conservative impact estimates.</p>
              </div>
              
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">Helps clients focus on one fix at a time</h3>
                <p className="text-sm text-text-muted">No overwhelm. One issue, one action, one week. Clear priorities for busy operators.</p>
              </div>
              
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <DollarSign className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">Easy to resell as part of your process</h3>
                <p className="text-sm text-text-muted">Include it in retainers or advisory packages. Adds value without extra work.</p>
              </div>
            </div>
            
            <p className="text-center text-text-muted text-sm mt-6">
              &quot;Most agencies use ReplyDriven internally before recommending fixes.&quot;
            </p>
          </div>
        </section>

        {/* Sample Report Section */}
        <section id="sample-report" className="py-16 px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 text-[#D4F34A] mb-4">
                <Mail className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wider">Sample Report</span>
              </div>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-4">
                What Your Client Sees Every Week
              </h2>
              <p className="text-text-muted">
                This is what your client receives - based on their store data.
              </p>
            </div>

            {/* Sample Report Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Report Card 1 */}
              <div className="bg-surface border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="text-xs font-medium text-red-400 uppercase tracking-wide">This Week&apos;s Profit Leak</span>
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-white">
                  Checkout Abandonment Spike
                </h3>
                <p className="text-sm text-text-muted mb-4">
                  Cart-to-checkout abandonment increased from 32% to 47% this week. This is costing an estimated ~$3,180 per week in lost revenue.
                </p>
                <div className="bg-[#0a0a0a] rounded-lg p-4 mb-4">
                  <p className="text-xs text-text-muted mb-2">Estimated Weekly Impact</p>
                  <p className="font-heading text-2xl font-bold text-red-400">~$3,180 / week</p>
                  <p className="text-xs text-text-muted mt-1">Estimated conservatively based on actual store data.</p>
                </div>
                <div className="border-t border-[#262626] pt-4">
                  <p className="text-xs text-[#D4F34A] font-medium mb-2">RECOMMENDED ACTION</p>
                  <p className="text-sm text-text-muted">
                    Review checkout page for new friction. Check if recent changes affected payment options or shipping clarity. Test checkout flow on mobile.
                  </p>
                </div>
              </div>

              {/* Report Card 2 */}
              <div className="bg-surface border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingCart className="w-5 h-5 text-yellow-400" />
                  <span className="text-xs font-medium text-yellow-400 uppercase tracking-wide">Bundle Opportunity</span>
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-white">
                  Frequently Bought Together Pattern Detected
                </h3>
                <p className="text-sm text-text-muted mb-4">
                  42% of customers who purchased Product A also viewed Product B within the same session, but only 8% added both to cart.
                </p>
                <div className="bg-[#0a0a0a] rounded-lg p-4 mb-4">
                  <p className="text-xs text-text-muted mb-2">Potential AOV Increase</p>
                  <p className="font-heading text-2xl font-bold text-[#D4F34A]">+$12-18 per order</p>
                </div>
                <div className="border-t border-[#262626] pt-4">
                  <p className="text-xs text-[#D4F34A] font-medium mb-2">RECOMMENDED ACTION</p>
                  <p className="text-sm text-text-muted">
                    Create a bundle or &quot;Frequently Bought Together&quot; section on the product page. Consider a small discount to protect AOV.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Agencies Position It */}
        <section className="py-16 px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight mb-6">
              How agencies position ReplyDriven
            </h2>
            
            <div className="bg-surface border border-[#D4F34A]/20 rounded-xl p-8">
              <p className="text-lg text-white italic mb-4">
                &quot;We monitor your store weekly and tell you exactly what to fix next.<br />
                No dashboards. No guessing.&quot;
              </p>
              <p className="text-sm text-text-muted">
                Many agencies include this as part of a monthly retainer or advisory package.
              </p>
            </div>
          </div>
        </section>

        {/* Who It's For / Not For */}
        <section className="py-16 px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <h3 className="font-heading text-xl font-bold mb-4 text-white flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#D4F34A]" />
                  Built for
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-text-muted">
                    <span className="text-[#D4F34A]">✓</span>
                    Shopify stores doing $20k+/month
                  </li>
                  <li className="flex items-start gap-3 text-text-muted">
                    <span className="text-[#D4F34A]">✓</span>
                    Founders who want clarity
                  </li>
                  <li className="flex items-start gap-3 text-text-muted">
                    <span className="text-[#D4F34A]">✓</span>
                    Operators with limited time
                  </li>
                </ul>
              </div>
              
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <h3 className="font-heading text-xl font-bold mb-4 text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Not for
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-text-muted">
                    <span className="text-red-400">×</span>
                    New stores (not enough data)
                  </li>
                  <li className="flex items-start gap-3 text-text-muted">
                    <span className="text-red-400">×</span>
                    Dropshipping experiments
                  </li>
                  <li className="flex items-start gap-3 text-text-muted">
                    <span className="text-red-400">×</span>
                    Analytics hobbyists
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              See what ReplyDriven flags in your store
            </h2>
            <p className="text-text-muted text-lg mb-8">
              Connect in under a minute. Get your first insight immediately.
            </p>
            
            <Button
              onClick={handleGetStarted}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-8 py-6 text-lg transition-transform hover:scale-105"
              data-testid="agency-cta-bottom-btn"
            >
              Get My First Weekly Insight (Free) <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <p className="text-sm text-text-muted mt-4">
              Takes under a minute to connect · Cancel anytime
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#262626]">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Logo showText linkTo="/" />
              <div className="flex items-center gap-6 text-sm text-text-muted">
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
                <a href="mailto:hello@replydriven.com" className="hover:text-white transition-colors">Contact</a>
              </div>
              <p className="text-sm text-text-muted">© {new Date().getFullYear()} ReplyDriven</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AgencyPage;
